import { Api, Api as defaultApi, ApiResponse } from './api.js';
import { TableHeader } from '../admin/view/view.js';
import { BasicEntity } from '../../types/entities';

export type ResourceUpdatedResponse = { id: number; rowCount: number, status: boolean };

/**
 * resource api calls
 * meant to cover about 95% of our needs
 */
export class ResourceApi<T extends BasicEntity> {
    constructor(
        public readonly url: string,
        public readonly resource: string,
        public readonly api: typeof Api,
    ) {}

    async fields(): Promise<ApiResponse<TableHeader>> {
        return this.api.api_get<TableHeader>(`${this.url}/api/resources/${this.resource}/fields`);
    }

    async get(lang: string|null = null): Promise<ApiResponse<T[]>> {
        return this.api.api_get<T[]>(`${this.url}/api/resources/${this.resource}`, lang);
    }

    async getFiltered(filter: Partial<T> & {__idList?: number[]}, idList: number[] = []): Promise<ApiResponse<T[]>> {
        if (idList.length > 0) filter.__idList = idList;
        return this.api.api_post<T[]>(`${this.url}/api/resources/${this.resource}/filter`, filter);
    }

    async getByID(id: number, lang: string|null = null): Promise<ApiResponse<T>> {
        return this.api.api_get<T>(`${this.url}/api/resources/${this.resource}/${id}`, lang);
    }

    async getBy(field: string, value: any, lang: string|null = null): Promise<ApiResponse<T[]>> {
        return this.api.api_get<T[]>(`${this.url}/api/resources/${this.resource}/${field}/${value}`, lang);
    }

    async getAutocomplete(
        field: string,
        value: any,
        lang: string|null = null,
    ): Promise<ApiResponse> {
        return this.api.api_get(`${this.url}/api/resources_autocomplete/${this.resource}/${field}/${value}`, lang);
    }

    async post(data: Partial<T>): Promise<ApiResponse<ResourceUpdatedResponse>> {
        return this.api.api_post<ResourceUpdatedResponse>(`${this.url}/api/resources/${this.resource}`, data);
    }

    async put(data: Partial<T>): Promise<ApiResponse<ResourceUpdatedResponse>> {
        if (data.hasOwnProperty('id')) {
            return this.api.api_put<ResourceUpdatedResponse>(`${this.url}/api/resources/${this.resource}/${data.id}`, data, null);
        }
        throw new Error('data must have an id, use post to add new data first');
    }

    async patch(data: Partial<T>): Promise<ApiResponse<ResourceUpdatedResponse>> {
        if (data.hasOwnProperty('id')) {
            return this.api.api_patch<ResourceUpdatedResponse>(`${this.url}/api/resources/${this.resource}/${data.id}`, data, null);
        }
        throw new Error('data must have an id, use post to add new data first');
    }

    async putLanguage(lang: string, data: Partial<T>): Promise<ApiResponse<ResourceUpdatedResponse>> {
        if (data.hasOwnProperty('id') && data.id !== null) {
            return this.api.api_put(`${this.url}/api/resources/${this.resource}/${data.id}/lang`, data, lang);
        }
        throw new Error('data must have an id');
    }

    async patchLanguage(lang: string, data: Partial<T>): Promise<ApiResponse<ResourceUpdatedResponse>> {
        if (data.hasOwnProperty('id') && data.id !== null) {
            return this.api.api_patch(`${this.url}/api/resources/${this.resource}/${data.id}/lang`, data, lang);
        }
        throw new Error('data must have an id');
    }

    async upsert(data: Partial<T>): Promise<ApiResponse<ResourceUpdatedResponse>> {
        return this.api.api_post(`${this.url}/api/resources/${this.resource}/upsert`, data);
    }

    async delete(id: number): Promise<ApiResponse> {
        return this.api.api_delete(`${this.url}/api/resources/${this.resource}/${id}`);
    }

    getApi(): typeof defaultApi {
        return this.api;
    }

    // enable aborting a running api request
    abortRunningRequest = this.api.abort_request.bind(this.api);
}
